import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  columnOf,
  formatDuration,
  modalityName,
  type HistoryGame,
  type HistoryRound,
} from '@bingo/shared';
import { ApiError, api } from '../../lib/api.js';
import { sendCommand } from '../../lib/commands.js';

/**
 * /historico/partida/:id — o resumo de uma partida, igual ao do telão.
 *
 * Os números do cabeçalho vêm do servidor, da MESMA função que fecha a
 * partida (`buildGameSummary`). Nada é recalculado aqui: se esta tela fizesse
 * a própria conta, um dia ela discordaria do que o salão viu na hora.
 */

const CLAIM_LABEL: Record<string, string> = {
  PENDENTE: 'em aberto',
  SIM: 'confirmada',
  NAO: 'não confirmada',
  MANUAL: 'confirmada manualmente',
  CANCELADA: 'cancelada',
};

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function dateLabel(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Uma rodada, dobrável. Fechada por padrão: a lista toda aberta não cabe no celular. */
function RoundCard({ round }: { round: HistoryRound }) {
  const [open, setOpen] = useState(false);

  return (
    <li className="history-round">
      <button
        type="button"
        className="history-round-head"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="history-round-title">
          <strong>Rodada {round.number}</strong>
          <small>{modalityName(round.modality)}</small>
        </span>
        <span className="history-round-stats">
          <span>{round.ballsCount} bolas</span>
          <span>{formatDuration(round.durationMs)}</span>
          {round.winners.length > 0 ? <span>🏆 {round.winners.length}</span> : null}
          <span aria-hidden="true">{open ? '▴' : '▾'}</span>
        </span>
      </button>

      {open ? (
        <div className="history-round-body">
          <dl className="history-facts">
            <div>
              <dt>Prêmio</dt>
              <dd>{round.prizeText ?? '—'}</dd>
            </div>
            <div>
              <dt>Início</dt>
              <dd>{timeLabel(round.startedAt)}</dd>
            </div>
            <div>
              <dt>Duração</dt>
              <dd>{formatDuration(round.durationMs)}</dd>
            </div>
            <div>
              <dt>Bolas próprias</dt>
              <dd>
                {round.ownBallsCount}
                {round.carriedCount > 0 ? ` (+${round.carriedCount} herdadas)` : ''}
              </dd>
            </div>
          </dl>

          <h4>Bolas, na ordem do sorteio</h4>
          {round.balls.length === 0 ? (
            <p className="hint-text">Nenhuma bola nesta rodada.</p>
          ) : (
            <ol className="history-balls">
              {round.balls.map((ball) => (
                <li
                  key={ball.ordinal}
                  // Herdada esmaecida: ela veio da rodada anterior e não foi
                  // cantada aqui.
                  className={ball.inherited ? 'history-ball inherited' : 'history-ball'}
                  style={{ borderColor: columnOf(ball.number).color }}
                  title={ball.inherited ? 'Herdada da rodada anterior' : timeLabel(ball.drawnAt)}
                >
                  <small>{columnOf(ball.number).letter}</small>
                  {String(ball.number).padStart(2, '0')}
                </li>
              ))}
            </ol>
          )}

          {round.winners.length > 0 ? (
            <>
              <h4>Ganhadores</h4>
              <ul className="history-winner-list">
                {round.winners.map((winner) => (
                  <li key={winner.id}>
                    {winner.photoUrl ? (
                      <img src={winner.photoUrl} alt={`Foto de ${winner.name}`} loading="lazy" />
                    ) : (
                      <span className="history-photo-empty" aria-hidden="true">
                        🏆
                      </span>
                    )}
                    <div>
                      <strong>{winner.name}</strong>
                      <small>
                        Cartela {winner.cardNumber ?? '—'}
                        {winner.winningBallNumber
                          ? ` · bola da vitória ${winner.winningBallNumber}`
                          : ''}
                      </small>
                      <small>
                        {winner.ballsAtWin} bolas no painel · {timeLabel(winner.confirmedAt)}
                      </small>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {round.claims.length > 0 ? (
            <>
              <h4>Conferências</h4>
              <ul className="history-claims">
                {round.claims.map((claim) => (
                  <li key={claim.id}>
                    <span className={`pill claim-${claim.status.toLowerCase()}`}>
                      {CLAIM_LABEL[claim.status] ?? claim.status}
                    </span>
                    <span>Cartela {claim.cardNumber ?? '—'}</span>
                    <span className="hint-text">
                      {timeLabel(claim.openedAt)}
                      {claim.resolvedAt ? ` → ${timeLabel(claim.resolvedAt)}` : ''}
                    </span>
                    {claim.patternWasStale ? (
                      <span className="hint-text">padrão já estava completo antes</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function HistoricoPartida() {
  const { gameId } = useParams<{ gameId: string }>();

  const [game, setGame] = useState<HistoryGame | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onDisplay, setOnDisplay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!gameId) return;

    void api<HistoryGame>(`/api/history/games/${gameId}`)
      .then(setGame)
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : 'Não foi possível carregar a partida.'),
      );
  }, [gameId]);

  useEffect(load, [load]);

  /** Projeta este resumo no telão pareado ao evento. */
  const showOnDisplay = async () => {
    if (!gameId) return;
    setBusy(true);

    try {
      await sendCommand(`/api/history/games/${gameId}/display`);
      setOnDisplay(true);
      setToast('Resumo no telão.');
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Não foi possível falar com o telão.');
    } finally {
      setBusy(false);
      window.setTimeout(() => setToast(null), 2600);
    }
  };

  const backToGame = async () => {
    if (!gameId) return;
    setBusy(true);

    try {
      await api(`/api/history/games/${gameId}/display`, { method: 'DELETE' });
      setOnDisplay(false);
      setToast('O telão voltou ao jogo.');
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Não foi possível falar com o telão.');
    } finally {
      setBusy(false);
      window.setTimeout(() => setToast(null), 2600);
    }
  };

  if (error) {
    return (
      <main className="shell">
        <div className="empty-card">
          <p className="error">{error}</p>
          <Link className="btn" to="/historico">
            Voltar ao histórico
          </Link>
        </div>
      </main>
    );
  }

  if (!game) {
    return (
      <main className="shell">
        <p className="hint-text">Carregando…</p>
      </main>
    );
  }

  return (
    <main className="shell">
      <header className="shell-head">
        <div>
          <h1>
            {game.eventName} · partida {game.gameNumber}
          </h1>
          <p className="hint-text">
            {dateLabel(game.startedAt)}
            {game.status === 'EM_ANDAMENTO' ? ' · em andamento' : ''}
          </p>
        </div>
        <Link className="btn" to="/historico">
          Histórico
        </Link>
      </header>

      {/* O mesmo trio de números do resumo do telão. */}
      <section className="history-totals">
        <div>
          <span>Tempo total</span>
          <strong>{formatDuration(game.totalMs)}</strong>
        </div>
        <div>
          <span>Soma das rodadas</span>
          <strong>{formatDuration(game.roundsMs)}</strong>
        </div>
        <div>
          <span>Rodadas jogadas</span>
          <strong>{game.roundsCount}</strong>
        </div>
      </section>

      <div className="history-cta">
        {onDisplay ? (
          <button type="button" className="btn" onClick={() => void backToGame()} disabled={busy}>
            ↩ Voltar ao jogo
          </button>
        ) : (
          <button
            type="button"
            className="btn primary"
            onClick={() => void showOnDisplay()}
            disabled={busy}
          >
            📺 Mostrar no telão
          </button>
        )}

        {/* Link comum, e não fetch + blob: o navegador do celular sabe
            baixar e abrir um CSV melhor do que qualquer código nosso. */}
        <a className="btn" href={`/api/history/games/${game.gameId}/export.csv`} download>
          ⬇ Baixar CSV
        </a>
      </div>

      {toast ? <p className="hint-text">{toast}</p> : null}

      <h2>Rodadas</h2>
      <ul className="history-rounds">
        {game.rounds.map((round) => (
          <RoundCard key={round.roundId} round={round} />
        ))}
      </ul>
    </main>
  );
}
